import { Paragraph, Table, TableCell, TableRow, VerticalAlign, WidthType } from 'docx';
import { EsrsReportMetric } from '../../Report.types';
import { tableBorders, TextStyle } from '../reportThemes';
import { uniq } from 'lodash';
import {
  addEmptySpace,
  findRowByCountryAndGender,
  findRowByTagName,
  totalEmployeesCountry,
} from '../report-helper-functions';
import { AggregatedMetricsTableData } from '../../../DisclosureRequirements';
import { S1_6CountryMetrics, S1_6GenderMetrics } from '../../ReportComponents';

type SubRow = NonNullable<EsrsReportMetric['tableData']['subRows']>[number];
type AnswersData = {
  title: string;
  answers: Record<string, number | string>;
}[];
const totalEmployeeRowTitle = 'Number of employees (head count / FTE)';
const commonMetricTags = {
  totalEmployeesBy: [
    { tagValue: 'Permanent employees', title: 'Number of permanent employees (head count / FTE)' },
    { tagValue: 'Temporary employees', title: 'Number of temporary employees (head count / FTE)' },
    {
      tagValue: 'Non-guaranteed hours employees',
      title: 'Number of non-guaranteed (head count / FTE)',
    },
  ],
  employeesContract2: [
    { tagValue: 'Full-time employees', title: 'Number of full-time employees (head count / FTE)' },
    { tagValue: 'Part-time employees', title: 'Number of part-time employees (head count / FTE)' },
  ],
};
const filterRowsMatchingTags = (
  subRows: EsrsReportMetric['tableData']['subRows'] | undefined,
  tagValue1: string,
  tagValue2: string
) => {
  return subRows?.filter(
    (subRow) =>
      subRow.tags?.some((tag) => tag.tagValue === tagValue1) &&
      subRow.tags?.some((tag) => tag.tagValue === tagValue2)
  );
};

const getDoubleLevelTagTotal = (
  row: EsrsReportMetric['tableData'] | undefined,
  tagValue1: string,
  tagValue2: string
) => {
  const nestedRows = row?.subRows
    ?.flatMap((subRow) => subRow?.subRows)
    ?.filter((s): s is SubRow => !!s);

  const tagValueRows = filterRowsMatchingTags(nestedRows, tagValue1, tagValue2);

  const tagValuesResults =
    tagValueRows
      ?.filter((r) => !r?.result?.isAnswerNotPresent)
      .map((r) => r?.result?.Year)
      .filter((res): res is number => res !== undefined) ?? [];

  if (!tagValuesResults.length) {
    return '';
  }

  return tagValuesResults.reduce((a, b) => a + b, 0);
};

const getTotal = (array: (string | number)[]) => {
  if (array.every((data) => data === '')) {
    return '';
  }
  return array.reduce((a, b) => {
    const v1 = typeof a === 'string' ? 0 : a;
    const v2 = typeof b === 'string' ? 0 : b;
    return v1 + v2;
  }, 0);
};

export const processGenderMetricsData = (
  metrics: EsrsReportMetric['tableData'][],
  totalEmployeeByContactGender: AggregatedMetricsTableData | undefined
) => {
  const sortedMetrics = metrics
    ?.filter(
      (m) =>
        S1_6GenderMetrics.includes(m.metric.reference) ||
        m.metric.reference === 'totalEmployeesContractGenderAndCountry'
    )
    ?.sort((metric1, metric2) => {
      const indexA = S1_6GenderMetrics.indexOf(
        metric1?.metric?.reference === 'totalEmployeesContractGenderAndCountry'
          ? 'totalEmployeesByContractGender'
          : metric1?.metric?.reference
      );
      const indexB = S1_6GenderMetrics.indexOf(
        metric2?.metric?.reference === 'totalEmployeesContractGenderAndCountry'
          ? 'totalEmployeesByContractGender'
          : metric2?.metric?.reference
      );

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const title = totalEmployeeRowTitle;
  const answers = {
    female: findRowByTagName(totalEmployeeByContactGender, 'Female') ?? '',
    male: findRowByTagName(totalEmployeeByContactGender, 'Male') ?? '',
    other: findRowByTagName(totalEmployeeByContactGender, 'Other gender') ?? '',
    notReported: findRowByTagName(totalEmployeeByContactGender, 'Not reported') ?? '',
    total: totalEmployeeByContactGender?.result?.isAnswerNotPresent
      ? ''
      : (totalEmployeeByContactGender?.result?.Year ?? 0),
  };
  return [
    { title, answers },
    sortedMetrics.flatMap((metric) => {
      const metricInfo = metric?.metric;
      if (
        metricInfo.reference === S1_6GenderMetrics[1] ||
        metricInfo.reference === 'totalEmployeesContractGenderAndCountry'
      ) {
        const rows = commonMetricTags.totalEmployeesBy.map(({ title, tagValue }) => {
          const female = getDoubleLevelTagTotal(totalEmployeeByContactGender, 'Female', tagValue);
          const male = getDoubleLevelTagTotal(totalEmployeeByContactGender, 'Male', tagValue);
          const other = getDoubleLevelTagTotal(
            totalEmployeeByContactGender,
            'Other gender',
            tagValue
          );
          const notReported = getDoubleLevelTagTotal(
            totalEmployeeByContactGender,
            'Not reported',
            tagValue
          );
          const answers = {
            female,
            male,
            other,
            notReported,
            total: getTotal([female, male, other, notReported]),
          };
          return { title, answers };
        });
        return rows;
      } else if (metricInfo.reference === 'employeesContract2') {
        const rows = commonMetricTags.employeesContract2.map(({ title, tagValue }) => {
          const female = getDoubleLevelTagTotal(metric, 'Female', tagValue);
          const male = getDoubleLevelTagTotal(metric, 'Male', tagValue);
          const other = getDoubleLevelTagTotal(metric, 'Other gender', tagValue);
          const notReported = getDoubleLevelTagTotal(metric, 'Not reported', tagValue);
          const answers = {
            female,
            male,
            other,
            notReported,
            total: getTotal([female, male, other, notReported]),
          };
          return { title, answers };
        });
        return rows;
      }
      return [];
    }),
  ]
    .flat()
    .slice(totalEmployeeByContactGender ? 0 : 1);
};

const createGenderTableContentRows = (dataRows: AnswersData) => {
  return dataRows.map((rowData) => {
    const { title, answers } = rowData;
    return new TableRow({
      children: [
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [new Paragraph({ text: title, style: TextStyle.body })],
        }),
        ...['female', 'male', 'other', 'notReported', 'total'].map(
          (key) =>
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  text: String(answers[key as keyof typeof answers]),
                  style: TextStyle.body,
                }),
              ],
            })
        ),
      ],
    });
  });
};

export const createS16GenderTable = (
  metrics: EsrsReportMetric['tableData'][],
  totalEmployeeByContactGender: AggregatedMetricsTableData | undefined
) => {
  const dataRows = processGenderMetricsData(metrics, totalEmployeeByContactGender);
  if (dataRows.length === 0) {
    return {};
  }
  const tableContentRows = createGenderTableContentRows(dataRows);

  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: '16%', type: WidthType.PERCENTAGE },
        children: [new Paragraph({ text: '', style: TextStyle.tableTitle })],
      }),
      ...['FEMALE', 'MALE', 'OTHER (*)', 'NOT DISCLOSED', 'TOTAL'].map(
        (header) =>
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            width: { size: '16%', type: WidthType.PERCENTAGE },
            children: [new Paragraph({ text: header, style: TextStyle.tableTitle })],
          })
      ),
    ],
  });

  return [
    addEmptySpace(300),
    new Table({
      columnWidths: [1666, 1666, 1666, 1666, 1666, 1666],
      margins: { top: 60, bottom: 60, right: 60, left: 60 },
      width: { size: '100%', type: WidthType.PERCENTAGE },
      borders: tableBorders,
      rows: [headerRow, ...tableContentRows],
    }),
  ].flat();
};

export const processCountryMetricsData = (
  metrics: EsrsReportMetric['tableData'][],
  countries: string[]
) => {
  const sortedMetrics = metrics
    ?.filter((m) => S1_6CountryMetrics.includes(m.metric.reference))
    ?.sort((metric1, metric2) => {
      const indexA = S1_6CountryMetrics.indexOf(metric1?.metric?.reference);
      const indexB = S1_6CountryMetrics.indexOf(metric2?.metric?.reference);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const title = totalEmployeeRowTitle;
  const totalEmployeesGenderAndCountryMetric = metrics.filter(
    (metric) => metric.metric.reference === 'totalEmployeesContractGenderAndCountry'
  );

  const answers = countries.reduce(
    (totals, country) => {
      let total: number | undefined;
      if (totalEmployeesGenderAndCountryMetric?.[0]?.subRows?.[0].tagType === 'Country') {
        total = totalEmployeesCountry(totalEmployeesGenderAndCountryMetric?.[0], country);
      } else {
        total = findRowByCountryAndGender(totalEmployeesGenderAndCountryMetric?.[0], country);
      }
      totals[country] = total ?? '';
      return totals;
    },
    {} as Record<string, number | string>
  );
  const total = getTotal(Object.values(answers));

  return [
    { title, answers: { ...answers, total } },
    sortedMetrics.flatMap((metric) => {
      const metricInfo = metric.metric;

      if (metricInfo.reference === 'totalEmployeesContractGenderAndCountry') {
        const rows = commonMetricTags.totalEmployeesBy.map(({ title, tagValue }) => {
          const answers = countries.reduce(
            (totals, country) => {
              const total = getDoubleLevelTagTotal(metric, country, tagValue);
              totals[country] = total;
              return totals;
            },
            {} as Record<string, number | string>
          );
          const total = getTotal(Object.values(answers));
          return { title, answers: { ...answers, total } };
        });
        return rows;
      } else if (metricInfo.reference === 'employeesContract2') {
        const rows = commonMetricTags.employeesContract2.map(({ title, tagValue }) => {
          const answers = countries.reduce(
            (totals, country) => {
              const total = getDoubleLevelTagTotal(metric, country, tagValue);
              totals[country] = total;
              return totals;
            },
            {} as Record<string, number | string>
          );
          const total = getTotal(Object.values(answers));
          return { title, answers: { ...answers, total } };
        });
        return rows;
      }
      return [];
    }),
  ]
    .flat()
    .slice(totalEmployeesGenderAndCountryMetric.length ? 0 : 1);
};

const createCountryTableContentRows = (dataRows: AnswersData, countries: string[]) => {
  return dataRows.map((rowData) => {
    const { title, answers } = rowData;
    return new TableRow({
      children: [
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [new Paragraph({ text: title, style: TextStyle.body })],
        }),
        ...[...countries, 'total'].map(
          (key) =>
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  text: String(answers[key as keyof typeof answers]),
                  style: TextStyle.body,
                }),
              ],
            })
        ),
      ],
    });
  });
};

export const createS16CountryTable = (metrics: EsrsReportMetric['tableData'][]) => {
  const countries = uniq(
    metrics
      .filter(
        (metric) =>
          metric.metric.reference === 'totalEmployeesContractGenderAndCountry' ||
          metric.metric.reference === 'employeesContract2'
      )
      .flatMap((metric) =>
        metric.metric.materialMetrics?.[0]?.materialMetricTags
          .find((tag) => tag.tagType === 'Country')
          ?.materialTagValues.flatMap((value) => value.tagValue)
      )
      .filter((t): t is string => !!t)
  );
  const columnsNb = (countries?.length + 2) / 100;
  const dataRows = processCountryMetricsData(metrics, countries);
  if (dataRows.length === 0) {
    return {};
  }

  const tableContentRows = createCountryTableContentRows(dataRows, countries);

  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: `${columnsNb}%`, type: WidthType.PERCENTAGE },
        children: [new Paragraph({ text: '', style: TextStyle.tableTitle })],
      }),
      ...countries.map(
        (header) =>
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            width: { size: `${columnsNb}%`, type: WidthType.PERCENTAGE },
            children: [new Paragraph({ text: header, style: TextStyle.tableTitle })],
          })
      ),
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: `${columnsNb}%`, type: WidthType.PERCENTAGE },
        children: [new Paragraph({ text: 'Total', style: TextStyle.tableTitle })],
      }),
    ],
  });

  return [
    addEmptySpace(300),
    new Table({
      columnWidths: Array(countries?.length + 2).fill(Math.floor(10000 / (countries?.length + 2))),
      margins: { top: 60, bottom: 60, right: 60, left: 60 },
      width: { size: '100%', type: WidthType.PERCENTAGE },
      borders: tableBorders,
      rows: [headerRow, ...tableContentRows],
    }),
  ].flat();
};
